import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";

const modifyTripSchema = z.object({
  tripId: z.string().min(1, "Trip ID is required"),
  modifications: z.string().min(1, "Modifications are required"),
});

type ModifyTripFormData = z.infer<typeof modifyTripSchema>;

const ModifyTrip = () => {
  const { toast } = useToast();
  
  const form = useForm<ModifyTripFormData>({
    resolver: zodResolver(modifyTripSchema),
    defaultValues: {
      tripId: "",
      modifications: "",
    },
  });

  const onSubmit = async (data: ModifyTripFormData) => {
    try {
      // TODO: Implement API call to modify trip
      console.log("Modifying trip:", data);
      
      toast({
        title: "Trip modification submitted",
        description: `Trip ${data.tripId} modification request has been submitted.`,
      });
      
      // Reset form after successful submission
      form.reset();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit trip modification. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-gray-100">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-center text-gray-900">
                Modify Trip
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 text-gray-900">
                  <FormField
                    control={form.control}
                    name="tripId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-900">Trip ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter trip ID"
                            className="bg-white text-gray-900 border-gray-300"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="modifications"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-900">Modifications</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter the modifications you want to make to this trip..."
                            className="min-h-[120px] bg-white text-gray-900 border-gray-300"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={form.formState.isSubmitting}
                  >
                    {form.formState.isSubmitting ? "Submitting..." : "Submit Modification"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ModifyTrip;